import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';

import { ConditionView, ContentField, Pagination, Typography } from '@/shared/ui';

import { queryKeys } from '@/api/queryKeys';
import { UserRequest, UserRequestFiltersOptionsV2, UserRequestScopeV2 } from '@/api/types';

import api from '@/api';

import { ServiceNames } from '../../../config/services';

import {
  DEFAULT_PAGE,
  DEFAULT_TOTAL,
  FILTER_QUERY_KEYS,
  PER_PAGE,
  UserRequestsFiltersState,
  UserRequestsTab,
} from './model';
import { UserRequestsList, UserRequestsListSkeleton, UserRequestsInformers, UserRequestsNavigation } from './ui';
import { UserRequestsEmptyListMessage } from './ui/UserRequestsEmptyListMessage';
import { UserRequestsFilters } from './ui/UserRequestsFilters';

import './UserRequests.scss';

type RequestsScope = Exclude<UserRequestsTab, 'draft'>;

export interface UserRequestProps {
  tab: UserRequestsTab;
}

const UserRequests: React.FC<UserRequestProps> = ({ tab }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { search } = useLocation();
  const prevTabRef = useRef<UserRequestsTab | null>(null);

  const { page, filters, hasFiltersApplied } = useMemo(() => {
    const sp = new URLSearchParams(search);
    const rawPage = Number(sp.get('page'));
    const page = Number.isFinite(rawPage) && rawPage > 0 ? rawPage : DEFAULT_PAGE;

    const parsedFilters: UserRequestsFiltersState = {
      channel: (sp.get('channel') as UserRequest['channel'] | null) || undefined,
      service_code: (sp.get('service_code') as UserRequest['service_code'] | null) || undefined,
      service_status_name: sp.get('service_status_name') || undefined,
      created_at_from: sp.get('created_at_from') || undefined,
      created_at_to: sp.get('created_at_to') || undefined,
    };

    const hasFiltersApplied = FILTER_QUERY_KEYS.some((key) => Boolean(sp.get(key)));

    return { page, filters: parsedFilters, hasFiltersApplied };
  }, [search]);

  const scope: RequestsScope | null = tab === 'draft' ? null : tab;
  const filtersScope: UserRequestScopeV2 = scope ?? 'active';

  const { data: userRequestsFiltersOptions, isLoading: areUserRequestsFiltersOptionsLoading } = useQuery({
    queryKey: queryKeys.userRequests.filters(filtersScope),
    queryFn: async () => (await api.getUserRequestFiltersV2(filtersScope))?.data,
    enabled: tab !== 'draft',
  });

  const { data: userRequestsV2Response, isLoading: areUserRequestsV2Loading } = useQuery({
    queryKey: queryKeys.userRequests.list(filtersScope, page, filters),
    queryFn: () => api.getUserRequestsV2({ scope: filtersScope, page, filters, per_page: PER_PAGE }),
    enabled: tab !== 'draft',
    staleTime: 0,
  });

  const { data: userRequestsDrafts = [], isLoading: areUserRequestsDraftsLoading } = useQuery({
    queryKey: queryKeys.userRequests.drafts(),
    queryFn: async () =>
      (await api.services.draft.getAll())?.data?.map((order) => ({
        ...order,
        title: ServiceNames[order.service_code],
      })),
    enabled: tab === 'draft',
    staleTime: 0,
  });

  const [cachedDeletedUserRequestsIds, setCachedDeletedUserRequestsIds] = useState<Array<UserRequest['id']>>([]);

  const isLoading = areUserRequestsDraftsLoading || areUserRequestsV2Loading || areUserRequestsFiltersOptionsLoading;

  const userRequestsOnCurrentTab = useMemo<UserRequest[]>(() => {
    if (tab === 'draft') {
      return (userRequestsDrafts ?? []).filter(({ id }) => !cachedDeletedUserRequestsIds.includes(id));
    }

    const list = (userRequestsV2Response?.data ?? []).map((order) => ({
      ...order,
      title: ServiceNames[order.service_code],
    }));

    return list.filter(({ id }) => !cachedDeletedUserRequestsIds.includes(id));
  }, [tab, userRequestsDrafts, userRequestsV2Response, cachedDeletedUserRequestsIds]);

  const pagination = tab === 'draft' ? null : userRequestsV2Response?.meta;

  useEffect(() => {
    if (prevTabRef.current === tab) return;
    prevTabRef.current = tab;
    setCachedDeletedUserRequestsIds([]);
    setSearchParams({ page: String(DEFAULT_PAGE) }, { replace: true });
  }, [tab, setSearchParams]);

  return (
    <section className="user-requests">
      <div className="container user-requests__container">
        <Typography variant="H1">Мої запити</Typography>

        <div className="user-requests__layout">
          <aside className="user-requests__aside">
            <UserRequestsNavigation scopes={userRequestsFiltersOptions?.scopes} />

            <ConditionView if={tab !== 'draft'}>
              <UserRequestsFilters
                tab={tab}
                filterOptions={userRequestsFiltersOptions as Nullable<UserRequestFiltersOptionsV2>}
                isLoadingOptions={areUserRequestsFiltersOptionsLoading || !userRequestsFiltersOptions}
              />
            </ConditionView>

            <UserRequestsInformers tab={tab} className="user-requests__informers user-requests__informers--desktop" />
          </aside>

          <section className="user-requests__content">
            <ConditionView if={!isLoading} else={<UserRequestsListSkeleton />}>
              <ConditionView
                if={!userRequestsOnCurrentTab.length}
                else={
                  <UserRequestsList
                    tab={tab}
                    userRequests={userRequestsOnCurrentTab}
                    storeDeletedUserRequest={(userRequestId): void =>
                      setCachedDeletedUserRequestsIds((prev) => [...prev, userRequestId])
                    }
                  />
                }
              >
                {hasFiltersApplied ? (
                  <ContentField
                    icon="no_result"
                    content={
                      <Typography color="graphite" textCenter>
                        За результатами фільтрації не знайдено жодного запиту. <br />
                        Будь ласка, очистіть фільтри.
                      </Typography>
                    }
                  />
                ) : (
                  <UserRequestsEmptyListMessage tab={tab} />
                )}
              </ConditionView>

              <ConditionView if={!!pagination?.total && !!pagination?.last_page && pagination.last_page > 1}>
                <Pagination
                  current={pagination?.current_page || DEFAULT_PAGE}
                  total={pagination?.total || DEFAULT_TOTAL}
                  pageSize={pagination?.per_page || PER_PAGE}
                  onChange={(nextPage): void => {
                    setSearchParams({ ...Object.fromEntries(searchParams), page: String(nextPage) }, { replace: true });
                  }}
                  containerClassName="user-requests__pagination"
                />
              </ConditionView>
            </ConditionView>

            <UserRequestsInformers tab={tab} className="user-requests__informers user-requests__informers--mobile" />
          </section>
        </div>
      </div>
    </section>
  );
};

export default UserRequests;
